import React, { useState } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Chip, IconButton, Collapse, Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';

import { aiDomains, usageByService } from '../data/mockData';

// Row component with collapsible details
const Row = ({ domain }) => {
  const [open, setOpen] = useState(false);
  
  // Find usage data for this domain
  const serviceData = usageByService.find(service => 
    service.service.toLowerCase().includes(domain.domain.split('.')[0].toLowerCase())
  );
  
  const usageCount = serviceData ? serviceData.count : 0;

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {domain.domain}
        </TableCell>
        <TableCell>
          <Chip label={domain.category} color="primary" size="small" />
        </TableCell>
        <TableCell>{domain.description}</TableCell>
        <TableCell>{usageCount}</TableCell>
        <TableCell>
          <Chip 
            label={usageCount > 0 ? "Active" : "Not Detected"} 
            color={usageCount > 0 ? "success" : "default"} 
            size="small" 
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Domain:</strong> {domain.domain}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Category:</strong> {domain.category}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Description:</strong> {domain.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>
                    <strong>First Detected:</strong> {usageCount > 0 ? "2025-03-01" : "N/A"}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Last Detected:</strong> {usageCount > 0 ? "2025-03-10" : "N/A"}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Data Risk Level:</strong> {domain.category === "LLM" ? "High" : "Medium"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Button variant="outlined" size="small" sx={{ mr: 1 }}>
                      View Access Logs
                    </Button>
                    <Button variant="outlined" size="small" color="error">
                      Mark as Sensitive
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const AIServices = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [filteredDomains, setFilteredDomains] = useState(aiDomains);
  
  const categories = ['all', ...new Set(aiDomains.map(domain => domain.category))];
  
  // Handle filtering
  const handleFilter = () => {
    let filtered = aiDomains;
    
    // Apply search term filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(domain => 
        domain.domain.toLowerCase().includes(lowercasedSearch) ||
        domain.description.toLowerCase().includes(lowercasedSearch)
      );
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(domain => domain.category === categoryFilter);
    }
    
    setFilteredDomains(filtered);
  };
  
  // Reset filters
  const handleReset = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setFilteredDomains(aiDomains);
  };
  
  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle category filter changes
  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
  };
  
  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFilter();
    }
  };
  
  // Handle adding new domain (mock functionality)
  const handleAddDomain = () => {
    alert('In a full implementation, this would open a dialog to add a new AI domain to monitor.');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        AI Services Catalog
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Search Domains"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-filter-label">Category</InputLabel>
              <Select
                labelId="category-filter-label"
                id="category-filter"
                value={categoryFilter}
                label="Category"
                onChange={handleCategoryChange}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleFilter}
              sx={{ mr: 1 }}
            >
              Apply Filters
            </Button>
            
            <Button 
              variant="outlined" 
              onClick={handleReset}
              sx={{ mr: 1 }}
            >
              Reset
            </Button>
            
            <Button 
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={handleAddDomain}
            >
              Add New Domain
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ width: '100%', overflow: 'hidden', bgcolor: 'background.paper' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Domain</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Usage Count</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDomains.map((domain) => (
                <Row key={domain.id} domain={domain} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AIServices;